import { useEffect, useRef } from 'react';
import { IDIOMAS_SITE, temConversorMoeda, temSeletorIdiomas } from '../../../utils/idiomaMoedaSite';
import { instalarIdiomaSite, prepararIdiomaDaUrl, widgetIdiomaCarregou } from '../../../utils/idiomaSite';
import { prepararMoedaDoIdioma } from '../../../utils/moedaSite';
import { useSiteOpcional } from '../../context/Info';
import styles from './SeletorIdiomas.module.css';

export { temSeletorIdiomas };

// Seletor de idiomas do GTranslate (widget "dwf" = lista com bandeiras, versão grátis). Estreou no
// cs2504 (05/10) no topo 3; em 21/10 passou a valer para todos os modelos pela opção do CRM
// (utils/idiomaMoedaSite.ts). O script de terceiro e a tradução no navegador só rodam em quem ligou.
// A tradução é só no cliente — o SSR/SEO segue em pt.
//
// UMA instância do widget por página: `wrapper="topo"` (cabeçalho do topo 3, visível) ou
// `wrapper="oculto"` (22/10: widget ESCONDIDO montado no _app — IdiomaOculto — que os itens de idioma
// dos menus acionam via utils/idiomaSite.trocarIdioma; substituiu a pílula flutuante de 21/10).

const GT_WIDGET_SRC = 'https://cdn.gtranslate.net/widgets/latest/dwf.js';
const GT_WRAPPERS = { topo: 'gtranslate_wrapper_topo', oculto: 'gtranslate_wrapper_oculto' } as const;
export type WrapperSeletor = keyof typeof GT_WRAPPERS;

// O tradutor (motor do Google) troca os nós de texto por <font> traduzidos. Quando o React
// depois remove ou insere ao lado de um texto que não está mais ali, o navegador lança
// NotFoundError e a página quebra (facebook/react#11538). Contorno conhecido: tolerar
// operações em nós que mudaram de pai. Instalado uma vez, e só nos sites com o seletor.
const protegerDomDoTradutor = () => {
    const w = window as any;
    if (w.__gtDomProtegido || typeof Node !== 'function') return;
    w.__gtDomProtegido = true;

    const removeChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) return child;
        return removeChild.call(this, child) as T;
    };

    const insertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
        if (ref && ref.parentNode !== this) return insertBefore.call(this, node, null) as T;
        return insertBefore.call(this, node, ref) as T;
    };
};

// O tradutor embrulha cada trecho traduzido num <font>. O título da ficha do imóvel (<h1> das
// galerias — <h2> no bloco mobile da gallery3 — e <h3> da barra fixa, classe adjust_font_size) é display:flex com o texto direto
// dentro: cada <font> virava um item do flex e o título quebrava em colunas ("Apartmentwith  2
// in Rio"). Enquanto a página está traduzida esses títulos passam a display:block — como só têm
// texto, em português o desenho é o mesmo. NÃO mexer no display dos <font>: a biblioteca do
// tradutor consulta se o elemento é "inline" para agrupar as frases (display:contents muda isso).
// Duas regras separadas de propósito: navegador sem :has() ainda aplica a primeira.
const GT_ESTILO_TITULOS = 'gt_titulos_traduzidos';
const TITULOS_FLEX = ['h1', 'h2', 'h3'].map((t) => `${t}[class*="adjust_font_size"]`);
const corrigirTitulosTraduzidos = () => {
    if (document.getElementById(GT_ESTILO_TITULOS)) return;
    const estilo = document.createElement('style');
    estilo.id = GT_ESTILO_TITULOS;
    estilo.textContent =
        `${TITULOS_FLEX.map((t) => `html[class*="translated-"] ${t}`).join(',')}{display:block}` +
        `${TITULOS_FLEX.map((t) => `${t}:has(> font)`).join(',')}{display:block}`;
    document.head.appendChild(estilo);
};

const SeletorIdiomas: React.FC<{ wrapper?: WrapperSeletor }> = ({ wrapper = 'topo' }) => {
    const ref = useRef<HTMLDivElement>(null);
    const classeWrapper = GT_WRAPPERS[wrapper];
    const site = useSiteOpcional();
    const converte = temConversorMoeda(site);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        protegerDomDoTradutor();
        corrigirTitulosTraduzidos();
        // moeda segue o clique no seletor — mesmo em página sem preço (ver utils/moedaSite)
        prepararMoedaDoIdioma(converte);
        // `?lang=` da URL manda no idioma (antes do script: a lib lê o idioma ao carregar) e o clique
        // direto no widget visível também vai para a URL (utils/idiomaSite)
        instalarIdiomaSite();
        prepararIdiomaDaUrl();

        // O seletor é remontado a cada página: o widget desenha de novo no wrapper novo e
        // prende outro <style> no <head> — limpa o anterior para não acumular.
        el.innerHTML = '';
        document.querySelectorAll('style.gtranslate_css').forEach((s) => s.remove());

        (window as any).gtranslateSettings = {
            default_language: 'pt',
            native_language_names: true,
            languages: [...IDIOMAS_SITE],
            alt_flags: { pt: 'brazil', en: 'usa' },
            wrapper_selector: `.${classeWrapper}`,
            switcher_horizontal_position: 'inline',
            flag_size: 24,
        };

        const script = document.createElement('script');
        script.src = GT_WIDGET_SRC;
        script.defer = true;
        script.onload = widgetIdiomaCarregou;
        document.body.appendChild(script);
        return () => {
            script.remove();
        };
    }, [classeWrapper, converte]);

    // sem espaço sobrando no topo: o HTML do 2504 continua byte a byte igual
    return <div ref={ref} className={`${classeWrapper} ${styles.wrapper}`} />;
};

export default SeletorIdiomas;
